import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { COMPANY, DEPARTMENTS, NAV, DESTINATIONS } from "@/lib/data";
import SimbaLogo from "@/components/SimbaLogo";

export default function Footer() {
  return (
    <footer className="bg-midnight text-linen mt-10" data-testid="site-footer">
      <div className="container-edge py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Brand + meta */}
          <div className="md:col-span-4">
            <SimbaLogo className="h-12 w-auto mb-4" />
            <address className="not-italic text-xs text-linen/70 leading-relaxed" data-testid="footer-address">
              <div className="text-linen/90">{COMPANY.legalName} — Reg. {COMPANY.registration}</div>
              <div>UK-based Air Zimbabwe GSA</div>
              <div>13 Hogarth Place, London</div>
              <div>SW5 0QT, United Kingdom</div>
            </address>
            <div className="mt-4 space-y-1 text-xs text-linen/70">
              <div className="flex items-center gap-2"><Clock className="w-3 h-3 stroke-1" /> {COMPANY.hours}</div>
            </div>
          </div>

          {/* Explore */}
          <div className="md:col-span-3">
            <div className="overline text-linen/60 mb-3 text-[10px]">Explore</div>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {NAV.filter((n) => n.to !== "/corporate-groups").map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-link-${n.to.replace(/\//g, "") || "home"}`}
                    className="text-xs text-linen/80 hover:text-terracotta transition-colors"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations */}
          <div className="md:col-span-2">
            <div className="overline text-linen/60 mb-3 text-[10px]">Destinations</div>
            <ul className="space-y-1.5">
              {DESTINATIONS.slice(0, 5).map((d) => (
                <li key={d.slug}>
                  <Link to={`/destinations/${d.slug}`} className="text-xs text-linen/80 hover:text-terracotta transition-colors">
                    {d.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/destinations" className="text-xs text-terracotta hover:underline">View all →</Link>
              </li>
            </ul>
          </div>

          {/* Contact desks — compact */}
          <div className="md:col-span-3">
            <div className="overline text-linen/60 mb-3 text-[10px]">Contact Centre</div>
            <ul className="space-y-1.5">
              {DEPARTMENTS.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-linen/80">{d.name}</span>
                  <a
                    href={`tel:${d.phone}`}
                    data-testid={`footer-phone-${d.id}`}
                    className="text-linen/60 hover:text-terracotta tabular-nums"
                  >
                    {d.phoneIntl}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-3 text-[10px] text-linen/45">
              All department emails available on the{" "}
              <Link to="/contact" className="underline hover:text-terracotta">Contact</Link>{" "}
              page.
            </div>
          </div>
        </div>

        <div className="rule bg-linen/10 my-6" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[10px] text-linen/45">
          <div>© {new Date().getFullYear()} Simba Limited (No. {COMPANY.registration}). All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-terracotta">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-terracotta">Terms</Link>
            <span>UK English</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
